import { forwardRef, type ButtonHTMLAttributes, type ReactElement, type ReactNode } from "react";

import { cn } from "../utils/cn";
import { Spinner } from "./Spinner";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "tertiary"
  | "destructive"
  | "destructive-ghost"
  | "icon";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  loading?: boolean;
  /** For a button that opens something (a panel, a menu): marks it while that's open.
   * Secondary variant only. Announce the state at the call site (`aria-expanded`). */
  active?: boolean;
}

const BASE =
  "inline-flex items-center justify-center gap-2 rounded-md font-medium whitespace-nowrap transition-colors select-none " +
  "disabled:cursor-default disabled:opacity-50 " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-app";

const SIZES: Record<ButtonSize, string> = {
  sm: "h-8 px-3 text-sm",
  md: "h-10 px-4 text-sm",
  lg: "h-12 px-5 text-[0.9375rem]",
};

const ICON_SIZES: Record<ButtonSize, string> = {
  sm: "h-8 w-8 p-0",
  md: "h-10 w-10 p-0",
  lg: "h-12 w-12 p-0",
};

const VARIANTS: Record<ButtonVariant, string> = {
  primary: "bg-primary-500 text-neutral-0 hover:bg-primary-600 active:bg-primary-700",
  secondary:
    "bg-neutral-0 text-neutral-800 border border-neutral-300 hover:bg-neutral-50 active:bg-neutral-100",
  tertiary: "bg-transparent text-primary-600 hover:bg-primary-50",
  destructive: "bg-danger-500 text-neutral-0 hover:bg-danger-600",
  "destructive-ghost": "bg-transparent text-danger-600 border border-danger-500 hover:bg-danger-50",
  icon: "bg-transparent text-neutral-600 hover:bg-neutral-100 hover:text-neutral-800",
};

const ACTIVE_SECONDARY =
  "bg-primary-50 text-primary-700 border border-primary-300 hover:bg-primary-100 active:bg-primary-100";

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "md",
    leftIcon,
    rightIcon,
    loading = false,
    active = false,
    disabled,
    children,
    className,
    type,
    ...rest
  },
  ref,
): ReactElement {
  const isIconOnly = variant === "icon";
  return (
    <button
      ref={ref}
      type={type ?? "button"}
      disabled={disabled ?? loading}
      className={cn(
        BASE,
        isIconOnly ? ICON_SIZES[size] : SIZES[size],
        variant === "secondary" && active ? ACTIVE_SECONDARY : VARIANTS[variant],
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner size={size === "lg" ? 16 : 14} /> : leftIcon}
      {!isIconOnly && children}
      {!loading && rightIcon}
    </button>
  );
});
